"use client";

import { Button, LoginHeroCarousel, LoginHeroSlide, Typography } from "@artha/ui";
import type { SocialProvider } from "@artha/types";
import { SocialAuthProvider } from "@artha/types";
import { useSocialLogin } from "@/modules/auth/hooks/useSocialLogin";
import { PageLayout } from "@/components/layout";
import { useT } from "@/lib/useT";
import { useSearchParams } from "next/navigation";
import {
    GoogleIcon,
    FacebookIcon,
    LinkedInIcon,
} from "@/components/icons";
import Hero1 from "@/components/icons/hero1";
import Hero2 from "@/components/icons/hero2";
import { getStoredReferralAttribution } from "@/modules/referrals/services/referralStorage";

type SocialProviderOption = {
    id: string;
    name: string;
    icon: React.ReactNode;
    provider?: SocialProvider;
    onClick?: () => void;
};

type PrimaryProviderConfig = {
    provider: SocialProvider;
    name: string;
    icon: React.ReactNode;
};

export default function LoginContent() {
    const { auth } = useT();
    const { loginWithProvider, loadingProvider, error } = useSocialLogin();
    const searchParams = useSearchParams();
    const referrer = searchParams.get("ref");
    const referralToken =
        searchParams.get("referralToken") ||
        getStoredReferralAttribution()?.attributionToken ||
        undefined;

    // Define primary provider configurations
    const primaryProviderConfigs: Record<string, PrimaryProviderConfig> = {
        [SocialAuthProvider.GOOGLE]: {
            provider: SocialAuthProvider.GOOGLE,
            name: "Google",
            icon: <GoogleIcon className="w-5 h-5" />,
        },
        [SocialAuthProvider.LINKEDIN]: {
            provider: SocialAuthProvider.LINKEDIN,
            name: "LinkedIn",
            icon: <LinkedInIcon className="text-button-primary w-5 h-5" />,
        },
        [SocialAuthProvider.FACEBOOK]: {
            provider: SocialAuthProvider.FACEBOOK,
            name: "Facebook",
            icon: <FacebookIcon className="text-[#1877F2] size-6" />,
        }
    };

    // Determine primary provider based on referrer, default to LinkedIn
    const primaryProvider = primaryProviderConfigs[referrer || SocialAuthProvider.LINKEDIN] || primaryProviderConfigs[SocialAuthProvider.LINKEDIN];

    // Define remaining social providers (excluding the primary one)
    const socialProviders: SocialProviderOption[] = [
        {
            id: SocialAuthProvider.GOOGLE,
            name: "Google",
            icon: <GoogleIcon />,
            provider: SocialAuthProvider.GOOGLE,
        },
        {
            id: SocialAuthProvider.LINKEDIN,
            name: "LinkedIn",
            icon: <LinkedInIcon className="text-button-primary w-5 h-5" />,
            provider: SocialAuthProvider.LINKEDIN,
        },
        {
            id: SocialAuthProvider.FACEBOOK,
            name: "Facebook",
            icon: <FacebookIcon className="text-[#1877F2] size-6" />,
            provider: SocialAuthProvider.FACEBOOK,
        }
    ].filter(provider => provider.provider !== primaryProvider.provider);

    const handleSocialClick = (option: SocialProviderOption) => {
        if (option.provider) {
            void loginWithProvider(option.provider, undefined, referralToken);
            return;
        }
        option.onClick?.();
    };
    const heroSlides = [
        <LoginHeroSlide key="hero-1">
            <Hero1 />
        </LoginHeroSlide>,
        <LoginHeroSlide key="hero-2">
            <Hero2 />
        </LoginHeroSlide>
    ]

    // const heroSlides = Array.from({ length: 3 }, (_, index) => (
    //     <LoginHeroSlide key={`login-hero-slide-${index}`} />
    // ));

    return (
        <PageLayout>
            <main className="ß">
                {/* Top Placeholder Image Area - 70% height */}
                <LoginHeroCarousel className="w-full shrink-0" slides={heroSlides} />

                {/* Content Area - Remaining 30% */}
                <div className="flex-1 flex flex-col px-5 mt-4 overflow-hidden">
                    <div className="mb-4">
                        <Typography variant="h3" className="text-[24px] text-black">
                            {auth("loginTitle")}
                        </Typography>
                        <Typography variant="p" className="text-[#475467]">
                            {auth("loginSubtitle")}
                        </Typography>
                    </div>

                    {/* Social Icons Row - Now shows remaining providers (2 when 3 total) */}
                    <div className="grid grid-cols-2 items-center w-full gap-3 self-stretch mb-4">
                        {socialProviders.map((provider) => (
                            <Button
                                key={provider.id}
                                size="lg"
                                className="bg-white"
                                onClick={() => handleSocialClick(provider)}
                                aria-label={auth("signInWith", { provider: provider.name })}
                                disabled={
                                    provider.provider
                                        ? loadingProvider === provider.provider
                                        : false
                                }
                            >
                                {provider.icon}
                                <span className="text-black text-sm">{provider.name}</span>
                            </Button>
                        ))}
                    </div>

                    {/* Primary Provider Button - Dynamic based on referrer */}
                    <div className="pb-8">
                        <Button
                            size="lg"
                            className="w-full flex h-[56px] rounded-xl text-white text-lg font-medium"
                            onClick={() => void loginWithProvider(primaryProvider.provider, undefined, referralToken)}
                            disabled={loadingProvider === primaryProvider.provider}
                        >
                            <span className="mr-2">
                                {auth("continueWith", { provider: primaryProvider.name })}
                            </span>
                            <div className="bg-white rounded-[4px] p-0.5 flex items-center justify-center h-6 w-6">
                                {primaryProvider.icon}
                            </div>
                        </Button>
                    </div>

                    {error && (
                        <p className="text-sm text-red-600 mb-4" role="alert">
                            {error}
                        </p>
                    )}
                </div>
            </main>
        </PageLayout>
    );
}
